/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback reducer
 *
 * @author Yakov Gushchin
 * @version 1.13.1
 * @since 1.12.0
 */

import produce from 'immer';
import { Reducer } from 'redux';

import { IOptionItemProps } from '../../components/common/Select/Select';
import {
    CASHBACK_GET_ACCOUNT_LIST_FAILURE,
    CASHBACK_GET_ACCOUNT_LIST_START,
    CASHBACK_GET_ACCOUNT_LIST_SUCCESS,
    CASHBACK_GET_META_FAILURE,
    CASHBACK_GET_META_START,
    CASHBACK_GET_META_SUCCESS,
    CASHBACK_GET_SERVICE_LIST_FAILURE,
    CASHBACK_GET_SERVICE_LIST_START,
    CASHBACK_GET_SERVICE_LIST_SUCCESS,
    CASHBACK_RESERVE_FAILURE,
    CASHBACK_RESERVE_START,
    CASHBACK_RESERVE_SUCCESS,
    CASHBACK_SET_ACCOUNT,
    CASHBACK_TOGGLE_AUTO_SPENDING_FAILURE,
    CASHBACK_TOGGLE_AUTO_SPENDING_START,
    CASHBACK_TOGGLE_AUTO_SPENDING_SUCCESS,
    pageCashbackInitialState,
} from './pageCashbackConstants';
import { ICashbackControlAvailableService, ICashbackControlConnectedService } from './types/cashbackControlTypes';
import { ICashbackMeta, TPageCashbackActions } from './types/cashbackTypes';

export interface IPageCashbackReducer {
    // account select change
    accountSelectCurrent: IOptionItemProps;
    accountSelectHasMore: boolean;
    accountSelectIsLoading: boolean;
    accountSelectList: IOptionItemProps[];
    // control tab
    controlServiceListAvailable: ICashbackControlAvailableService[];
    controlServiceListConnected: ICashbackControlConnectedService[];
    controlServiceListIsLoading: boolean;
    controlServiceListIsError: boolean;
    // meta info
    meta: ICashbackMeta;
    clientId?: string;

    isError: boolean;
    isLoading: boolean;

    reserveError: string[];
}

const cashbackReducer: Reducer<IPageCashbackReducer, TPageCashbackActions> =
    (state = pageCashbackInitialState, action: TPageCashbackActions) =>
        produce(state, (draft) => {
            switch (action.type) {

                /* Get Cashback Meta */

                case CASHBACK_GET_META_START:
                    draft.isLoading = true;
                    draft.isError = false;
                    draft.clientId = action.payload.clientId;
                    break;
                case CASHBACK_GET_META_SUCCESS:
                    draft.isLoading = false;
                    draft.meta = action.payload.meta;
                    draft.accountSelectCurrent = action.payload.accountSelectCurrent;
                    break;
                case CASHBACK_GET_META_FAILURE:
                    draft.isLoading = false;
                    draft.isError = true;
                    break;

                /* Cashback Account Select */

                case CASHBACK_GET_ACCOUNT_LIST_START:
                    draft.accountSelectIsLoading = true;
                    break;
                case CASHBACK_GET_ACCOUNT_LIST_SUCCESS:
                    draft.accountSelectHasMore = action.payload.accountSelectHasMore;
                    draft.accountSelectList = action.payload.accountSelectList;
                    draft.accountSelectIsLoading = false;
                    break;
                case CASHBACK_GET_ACCOUNT_LIST_FAILURE:
                    draft.accountSelectIsLoading = false;
                    break;

                case CASHBACK_SET_ACCOUNT:
                    draft.accountSelectCurrent = action.payload;
                    break;

                /* Control tab actions */

                case CASHBACK_GET_SERVICE_LIST_START:
                    draft.controlServiceListIsLoading = true;
                    draft.controlServiceListIsError = false;
                    break;
                case CASHBACK_GET_SERVICE_LIST_SUCCESS:
                    draft.controlServiceListIsLoading = false;
                    draft.controlServiceListAvailable = action.payload.controlServiceListAvailable;
                    draft.controlServiceListConnected = action.payload.controlServiceListConnected;
                    break;
                case CASHBACK_GET_SERVICE_LIST_FAILURE:
                    draft.controlServiceListIsLoading = false;
                    draft.controlServiceListIsError = true;
                    draft.controlServiceListAvailable = [];
                    draft.controlServiceListConnected = [];
                    break;

                case CASHBACK_RESERVE_START:
                    draft.reserveError = [];
                    break;
                case CASHBACK_RESERVE_SUCCESS:
                    draft.meta.balance = action.payload.balance;
                    draft.meta.balanceReserved = action.payload.balanceReserved;
                    draft.meta.balanceLost = action.payload.balanceLost;
                    break;
                case CASHBACK_RESERVE_FAILURE:
                    draft.reserveError = action.payload;
                    break;

                case CASHBACK_TOGGLE_AUTO_SPENDING_START:
                    // change switch on ui
                    draft.meta.isAutoSpendingEnabled = !action.payload.isAutoSpendingEnabled;
                    break;
                case CASHBACK_TOGGLE_AUTO_SPENDING_SUCCESS:
                    // set value from server
                    draft.meta.isAutoSpendingEnabled = action.payload.isAutoSpendingEnabled;
                    break;
                case CASHBACK_TOGGLE_AUTO_SPENDING_FAILURE:
                    // revert ui change
                    draft.meta.isAutoSpendingEnabled = !draft.meta.isAutoSpendingEnabled;
                    break;

                default:
                    break;
            }
        });

export default cashbackReducer;
